import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';

import paths from '@src/paths';
import { toast } from '@src/components/core/toaster';
import ArchiveIcon from '@mui/icons-material/Archive';
import { listProspects, updateProspect, convertProspect, convertProspectGroup, archiveProspect } from '@src/api/actions/prospects';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'converted', label: 'Converted' },
  { value: 'declined', label: 'Declined' },
];

const STATUS_COLORS = {
  new: 'info',
  contacted: 'warning',
  converted: 'success',
  declined: 'default',
};

const fmtCurrency = (n) => {
  if (n == null || n === 0) return '—';
  return `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function ProspectListPage() {
  const navigate = useNavigate();
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [convertDialog, setConvertDialog] = useState(null); // token string or { group: groupToken }
  const [converting, setConverting] = useState(false);
  const [archiveDialog, setArchiveDialog] = useState(null); // token string
  const [archiving, setArchiving] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  const loadProspects = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listProspects({ status: statusFilter || undefined, search: search || undefined });
      setProspects(res?.data?.results || []);
    } catch {
      toast.error('Failed to load prospects');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    loadProspects();
  }, [loadProspects]);

  // Group prospects: multi-brand groups + singles
  const { groups, singles } = useMemo(() => {
    const groupMap = {};
    const singles = [];
    for (const p of prospects) {
      if (p.company_group_token) {
        if (!groupMap[p.company_group_token]) {
          groupMap[p.company_group_token] = [];
        }
        groupMap[p.company_group_token].push(p);
      } else {
        singles.push(p);
      }
    }
    // Sort groups by first prospect's created_at descending
    const groups = Object.entries(groupMap)
      .map(([token, members]) => ({ token, members, created_at: members[0]?.created_at }))
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return { groups, singles };
  }, [prospects]);

  const toggleGroup = (token) => {
    setExpandedGroups((prev) => ({ ...prev, [token]: !prev[token] }));
  };

  const handleStatusChange = async (token, newStatus) => {
    try {
      await updateProspect(token, { status: newStatus });
      setProspects((prev) =>
        prev.map((p) => (p.token === token ? { ...p, status: newStatus } : p))
      );
      toast.success('Status updated');
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleConvert = async () => {
    if (!convertDialog) return;
    setConverting(true);
    try {
      if (convertDialog.group) {
        const res = await convertProspectGroup(convertDialog.group);
        const clients = res?.data?.clients || [];
        toast.success(`Converted ${clients.length} brands into clients!`);
        setConvertDialog(null);
        loadProspects();
      } else {
        const res = await convertProspect(convertDialog);
        const clientSlug = res?.data?.client_slug;
        toast.success('Prospect converted to client!');
        setConvertDialog(null);
        if (clientSlug) {
          navigate(`${paths.clientHub(clientSlug)}?openContract=1`);
        } else {
          loadProspects();
        }
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to convert prospect';
      toast.error(detail);
    } finally {
      setConverting(false);
    }
  };

  const handleArchive = async () => {
    if (!archiveDialog) return;
    setArchiving(true);
    try {
      await archiveProspect(archiveDialog);
      setProspects((prev) => prev.filter((p) => p.token !== archiveDialog));
      toast.success('Prospect archived');
      setArchiveDialog(null);
    } catch {
      toast.error('Failed to archive prospect');
    } finally {
      setArchiving(false);
    }
  };

  const isGroupConvert = convertDialog?.group;
  const groupForDialog = isGroupConvert
    ? groups.find((g) => g.token === convertDialog.group)
    : null;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Prospects</Typography>
        <Button startIcon={<RefreshIcon />} onClick={loadProspects} size="small">Refresh</Button>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 140 }}
        >
          {STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="Search"
          placeholder="Company, name, or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 250 }}
          onKeyDown={(e) => e.key === 'Enter' && loadProspects()}
        />
      </Stack>

      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={32} /></Box>
          ) : (prospects.length === 0) ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary">
                No prospects yet. Share your <strong>/get-started</strong> link to start receiving prospects.
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Company / Brand</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Verticals</TableCell>
                  <TableCell align="right">Budget</TableCell>
                  <TableCell align="right">ZIPs</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Multi-brand groups */}
                {groups.map((group) => {
                  const first = group.members[0];
                  const expanded = !!expandedGroups[group.token];
                  const totalBudget = group.members.reduce((s, p) => s + Number(p.budget || 0), 0);
                  const totalZips = group.members.reduce((s, p) => s + (p.zip_data || []).length, 0);
                  const allConverted = group.members.every((p) => p.status === 'converted');
                  const hasUnconverted = group.members.some((p) => p.status !== 'converted');

                  return (
                    <React.Fragment key={group.token}>
                      {/* Group header row */}
                      <TableRow
                        hover
                        sx={{ bgcolor: '#f8f9fb', cursor: 'pointer' }}
                        onClick={() => toggleGroup(group.token)}
                      >
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <GroupWorkIcon fontSize="small" color="primary" />
                            <Box>
                              <Typography variant="body2" fontWeight={700}>{first.company_name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {group.members.length} brands
                              </Typography>
                            </Box>
                            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{first.contact_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{first.contact_email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {[...new Set(group.members.flatMap((p) => p.verticals || []))].map((v) => (
                              <Chip key={v} label={v} size="small" variant="outlined" />
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">{fmtCurrency(totalBudget)}</TableCell>
                        <TableCell align="right">{totalZips}</TableCell>
                        <TableCell>
                          <Chip
                            label={allConverted ? 'converted' : 'new'}
                            size="small"
                            color={allConverted ? 'success' : 'info'}
                          />
                        </TableCell>
                        <TableCell>{fmtDate(first.created_at)}</TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Fit Report">
                              <IconButton size="small" color="info" onClick={(e) => { e.stopPropagation(); navigate(paths.prospectFitReport(first.token)); }}>
                                <AssessmentIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {hasUnconverted && (
                              <Tooltip title="Convert All Brands to Clients">
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<PersonAddIcon />}
                                  onClick={(e) => { e.stopPropagation(); setConvertDialog({ group: group.token }); }}
                                  sx={{ textTransform: 'none', fontSize: 12 }}
                                >
                                  Convert All
                                </Button>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>

                      {/* Expanded brand rows */}
                      {expanded && group.members.map((p) => (
                        <TableRow key={p.token} hover sx={{ bgcolor: '#fafbfc' }}>
                          <TableCell sx={{ pl: 6 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {p.brand_name || p.company_name}
                            </Typography>
                          </TableCell>
                          <TableCell />
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {(p.verticals || []).map((v) => (
                                <Chip key={v} label={v} size="small" variant="outlined" />
                              ))}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">{fmtCurrency(p.budget)}</TableCell>
                          <TableCell align="right">{(p.zip_data || []).length}</TableCell>
                          <TableCell>
                            <Chip label={p.status} size="small" color={STATUS_COLORS[p.status] || 'default'} />
                          </TableCell>
                          <TableCell>{fmtDate(p.created_at)}</TableCell>
                          <TableCell align="center">
                            {p.status !== 'converted' && (
                              <Tooltip title="Convert this brand only">
                                <IconButton size="small" color="primary" onClick={() => setConvertDialog(p.token)}>
                                  <PersonAddIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Archive">
                              <IconButton size="small" color="default" onClick={(e) => { e.stopPropagation(); setArchiveDialog(p.token); }}>
                                <ArchiveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })}

                {/* Single-brand prospects */}
                {singles.map((p) => (
                  <TableRow key={p.token} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{p.company_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{p.contact_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.contact_email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {(p.verticals || []).map((v) => (
                          <Chip key={v} label={v} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{fmtCurrency(p.budget)}</TableCell>
                    <TableCell align="right">{(p.zip_data || []).length}</TableCell>
                    <TableCell>
                      <Chip label={p.status} size="small" color={STATUS_COLORS[p.status] || 'default'} />
                    </TableCell>
                    <TableCell>{fmtDate(p.created_at)}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Fit Report">
                          <IconButton size="small" color="info" onClick={() => navigate(paths.prospectFitReport(p.token))}>
                            <AssessmentIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {p.status !== 'converted' && (
                          <Tooltip title="Convert to Client">
                            <IconButton size="small" color="primary" onClick={() => setConvertDialog(p.token)}>
                              <PersonAddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {p.status !== 'converted' && p.status !== 'contacted' && (
                          <Tooltip title="Mark Contacted">
                            <IconButton size="small" onClick={() => handleStatusChange(p.token, 'contacted')}>
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Archive">
                          <IconButton size="small" color="default" onClick={() => setArchiveDialog(p.token)}>
                            <ArchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Convert Confirmation Dialog */}
      <Dialog open={!!convertDialog} onClose={() => setConvertDialog(null)}>
        <DialogTitle>
          {isGroupConvert ? 'Convert All Brands to Clients' : 'Convert Prospect to Client'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isGroupConvert ? (
              <>
                This will create <strong>{groupForDialog?.members?.length || 0} clients</strong> under
                a new parent company "<strong>{groupForDialog?.members?.[0]?.company_name}</strong>".
                Each brand becomes its own client with separate onboarding, creative, and budgets.
              </>
            ) : (
              'This will create a new client from this prospect\'s information. You can then set up their territories, projections, and begin the onboarding process.'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialog(null)}>Cancel</Button>
          <Button onClick={handleConvert} variant="contained" disabled={converting}>
            {converting ? 'Converting...' : isGroupConvert ? 'Convert All' : 'Convert'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={!!archiveDialog} onClose={() => setArchiveDialog(null)}>
        <DialogTitle>Archive Prospect</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to archive this prospect? It will be removed from the table. You can restore it later from the admin panel.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveDialog(null)}>Cancel</Button>
          <Button onClick={handleArchive} variant="contained" color="error" disabled={archiving}>
            {archiving ? 'Archiving...' : 'Archive'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
